import React, { createContext, useState, useEffect, useContext } from 'react';
import { apiRoutes } from '../shared/apiRoutes';
import apiService from '../services/ApiService';
import { Role } from '../auth/roles';
import { applyTheme, normalizeTheme, readAppliedTheme, rememberTheme, rememberedTheme, ThemeMode } from '../theme/applyTheme';

export type UserConfig = Record<string, unknown>;

const emptyUserConfig = (): UserConfig => ({});

const parseUserConfig = (raw: unknown): UserConfig => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return { ...(raw as UserConfig) };
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as UserConfig;
      }
    } catch {
      return emptyUserConfig();
    }
  }
  return emptyUserConfig();
};

interface AuthContextType {
  authenticated: boolean | null;
  userRoles: (Role | string)[];
  userId: number | null;
  partnerId: number | null; // Partner-specific ID if user is a Partner
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => Promise<void>;
  userConfig: UserConfig;
  updateUserConfig: (patch: UserConfig) => Promise<void>;
        login: () => Promise<void>;
  logout: () => void;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [userRoles, setUserRoles] = useState<(Role | string)[]>([]);
  const [userId, setUserId] = useState<number | null>(null);
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [theme, setThemeState] = useState<ThemeMode>(rememberedTheme() ?? 'default');
  const [userConfig, setUserConfig] = useState<UserConfig>(emptyUserConfig);

  const fetchUserProfile = async () => {
    try {
      const response = await apiService.get(apiRoutes.auth.profile);
      const profileData = (response as any).data || response;
      
      // Extract user ID
      const id = profileData.id || profileData.userId || profileData.user_id || null;
      setUserId(id);
      
      // Extract roles - handle different response formats
      let roles: (Role | string)[] = [];
      if (profileData.role) {
        // Single role as string
        roles = [profileData.role];
      } else if (profileData.roles && Array.isArray(profileData.roles)) {
        // Array of roles
        roles = profileData.roles;
      } else if (profileData.roleName) {
        // Alternative field name
        roles = [profileData.roleName];
      } else if (profileData.userRole) {
        // Another alternative
        roles = [profileData.userRole];
      }
      
      // Normalize role names (handle case variations)
      roles = roles.map(role => {
        const roleStr = String(role).trim();
        // Map common variations to standard role names
        const compact = roleStr.toLowerCase().replace(/[\s_-]+/g, '');
        if (compact === 'partner') return Role.PARTNER;
        if (compact === 'admin') return Role.ADMIN;
        if (compact === 'owner') return Role.OWNER;
        if (compact === 'user') return Role.USER;
        if (compact === 'angadiyaadmin') return Role.ANGADIYA_ADMIN;
        if (compact === 'angadiyauser') return Role.ANGADIYA_USER;
        if (compact === 'angadiya') return Role.ANGADIYA;
        return roleStr; // Return as-is if no match
      });
      
      setUserRoles(roles);
      
      // Extract partner party ID if user is a Partner (bound tenant party)
      if (roles.includes(Role.PARTNER) || roles.some(r => String(r).toLowerCase() === 'partner')) {
        const pId =
          profileData.partyId ??
          profileData.PartyId ??
          profileData.partnerId ??
          profileData.partner_id ??
          null;
        setPartnerId(pId != null && Number(pId) > 0 ? Number(pId) : null);
      } else {
        setPartnerId(null);
      }

      const rawTheme = profileData.theme ?? profileData.Theme;
      const nextTheme = rawTheme != null && String(rawTheme).trim() !== ''
        ? normalizeTheme(rawTheme)
        : (rememberedTheme() ?? 'default');
      rememberTheme(nextTheme);
      applyTheme(nextTheme);
      setThemeState(nextTheme);
      setUserConfig(parseUserConfig(profileData.userConfig ?? profileData.UserConfig));
      
      setAuthenticated(true);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setAuthenticated(false);
      setUserRoles([]);
      setUserId(null);
      setPartnerId(null);
      setUserConfig(emptyUserConfig());
      const savedTheme = rememberedTheme() ?? 'default';
      applyTheme(savedTheme);
      setThemeState(savedTheme);
    }
  };

  useEffect(() => {
    // Check authentication status first
    apiService.get(apiRoutes.auth.status)
      .then((res) => {
        if (res) {
          // If authenticated, fetch user profile to get roles
          fetchUserProfile();
        } else {
          const savedTheme = rememberedTheme() ?? 'default';
          applyTheme(savedTheme);
          setThemeState(savedTheme);
          setAuthenticated(false);
        }
      })
      .catch(() => {
        const savedTheme = rememberedTheme() ?? 'default';
        applyTheme(savedTheme);
        setThemeState(savedTheme);
        setAuthenticated(false);
      });
  }, []);

  const login = async () => {
    // Profile includes the saved theme and is applied before the app is shown.
    await fetchUserProfile();
  };

  const setTheme = async (next: ThemeMode) => {
    const normalized = normalizeTheme(next);
    const previous = readAppliedTheme();
    applyTheme(normalized);
    setThemeState(normalized);
    rememberTheme(normalized);
    try {
      await apiService.put(apiRoutes.auth.theme, { theme: normalized });
    } catch (error) {
      const status = (error as { status?: number })?.status;
      // The running API build has no theme route yet. Keep the choice on this browser.
      if (status === 404 || status == null) {
        return;
      }
      applyTheme(previous);
      setThemeState(previous);
      rememberTheme(previous);
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: 'Could not save the theme.', type: 'error' },
      }));
    }
  };

  const updateUserConfig = async (patch: UserConfig) => {
    const previous = userConfig;
    setUserConfig({ ...userConfig, ...patch });
    try {
      const response = await apiService.put(apiRoutes.auth.userConfig, patch);
      const body = (response as { userConfig?: unknown; data?: { userConfig?: unknown } }) ?? {};
      const saved = body.userConfig ?? body.data?.userConfig;
      if (saved !== undefined) {
        setUserConfig(parseUserConfig(saved));
      }
    } catch (error) {
      setUserConfig(previous);
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: 'Could not save the display choice.', type: 'error' },
      }));
      throw error;
    }
  };

  const logout = () => {
    // Clear authentication state
    setAuthenticated(false);
    setUserRoles([]);
    setUserId(null);
    setPartnerId(null);
    setUserConfig(emptyUserConfig());
    const savedTheme = rememberedTheme() ?? 'default';
    applyTheme(savedTheme);
    setThemeState(savedTheme);
    
    // Clear any stored tokens or auth data
    try {
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
    } catch (error) {
      console.error('Error clearing storage on logout:', error);
    }
  };

  const refreshUserProfile = async () => {
    await fetchUserProfile();
  };

  return (
    <AuthContext.Provider value={{ 
      authenticated, 
      userRoles, 
      userId, 
      partnerId,
      theme,
      setTheme,
      userConfig,
      updateUserConfig,
      login, 
      logout,
      refreshUserProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth: () => AuthContextType = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
